import { useQuery } from "@apollo/client";
import { useAtom } from "jotai";
import { Italiana } from "next/font/google";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";

import { initializeApollo } from "@/apollo-client";
import Button from "@/components/core/button";
import Loading from "@/components/core/loading";
import CategoryDropdown from "@/components/ui/header-dropdown";
import HeaderMenu from "@/components/ui/header-menu";
import Collapsible from "@/components/ui/header-menu/collapsible";
import CREATE_SHORT_URL from "@/graphql/queries/createShortURL";
import Navigation from "@/graphql/queries/navigation";
import {
  NavigatioCategory,
  NavigationQuery,
  NavigationRow,
} from "@/graphql/types/graphql";
import useCollection from "@/hooks/useCollection";
import useMe from "@/hooks/useMe";
import {
  preferenceBannerShowedAtom,
  preferenceOriginAtom,
} from "@/hooks/useTracker";
import useTracker from "@/hooks/useTracker";
import HeartLineBlackThinIcon from "@/public/heart-line-black-thin.svg";
import MenuIcon from "@/public/menu.svg";
import { SOCIAL_LINKS } from "@/utils/const";
import { encodeURL } from "@/utils/index";

const italiana = Italiana({
  subsets: ["latin"],
  weight: "400",
});

const apolloClient = initializeApollo();

const Header = ({
  showLogoOnly = false,
  alwaysShowLogo = true,
  hasPaddingBottom = false,
}: {
  hasPaddingBottom?: boolean;
  position?: string;
  alwaysShowLogo?: boolean;
  showLogoOnly?: boolean;
}) => {
  const [show, setShow] = useState(false);
  const [isShareCopied, setIsCopied] = useState(false);
  const [isShareLoading, setIsShareLoading] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const { me, logout, goLogin, featureFlags } = useMe();
  const { collections } = useCollection();
  const [showDropdown, setShowDropdown] = useState(-1);
  const router = useRouter();
  const { preferenceBannerViewEvent, preferenceBannerClickEvent } =
    useTracker();
  const [, setPreferenceOrigin] = useAtom(preferenceOriginAtom);
  const [preferenceBannerShowed, setPreferenceBannerShowed] = useAtom(
    preferenceBannerShowedAtom
  );
  const [mobileMenu, setMobileMenu] = useState("");

  const { data } = useQuery<NavigationQuery>(Navigation);

  const navigation = useMemo(() => {
    return data?.navigation || [];
  }, [data]);

  const currentNavigation = useMemo(() => {
    return navigation.find((item) => {
      return item.title === mobileMenu;
    });
  }, [navigation, mobileMenu]);

  const goPage = async (page: string) => {
    if (page === "login") {
      await router.push(`/login?redirectURL=${router.asPath}`);
    } else {
      await router.push(`/${page}`);
    }
    handleClose();
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleShare = async () => {
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set("utm_medium", "influencer_share");
    shareUrl.searchParams.set("utm_content", me.id);
    shareUrl.searchParams.set("utm_term", window.location.href);

    let urlString = shareUrl.toString();
    setIsShareLoading(true);

    try {
      const { data } = await apolloClient.query({
        query: CREATE_SHORT_URL,
        variables: { url: shareUrl.toString() },
        fetchPolicy: "network-only",
      });

      if (data && data.createShortURL) {
        urlString = data.createShortURL;
      }
    } catch (error) {
      console.error("Error creating short URL:", error);
    } finally {
      setIsShareLoading(false);
    }

    try {
      // Try clipboard API first
      await navigator.clipboard.writeText(urlString);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      // If clipboard fails and share is available, use share API
      if (navigator.share) {
        try {
          await navigator.share({
            url: urlString,
          });
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        } catch (shareErr) {
          console.error("Share failed:", shareErr);
        }
      } else {
        console.error("Failed to copy URL:", err);
      }
    }
  };

  const itemsCount = useMemo(() => {
    return collections.length > 0
      ? collections[0].totalCount > 99
        ? "99+"
        : collections[0].totalCount
      : 0;
  }, [collections]);

  const showPreferenceBanner = useMemo(() => {
    return (
      featureFlags?.showPreferenceBanner &&
      router.route !== "/preference" &&
      router.route !== "/login"
    );
  }, [featureFlags?.showPreferenceBanner, router.route]);

  const handleMobileOptionClick = (option: NavigationRow) => {
    if (option.page) {
      router.push(`/${option.page}`);
      setShow(false);
    } else {
      setMobileMenu(option.title);
    }
  };

  const closeMobileMenu = () => {
    setShow(false);
    setMobileMenu("");
  };

  useEffect(() => {
    if (showPreferenceBanner && !preferenceBannerShowed) {
      preferenceBannerViewEvent();
      setPreferenceBannerShowed(true);
    }
  }, [showPreferenceBanner]);

  return (
    <>
      <div className="header">
        {showPreferenceBanner && (
          <div className="tips">
            Want a more personalized experience?
            <div
              className="tips-link"
              onClick={() => {
                setPreferenceOrigin("banner");
                preferenceBannerClickEvent();
                router.push("/preference");
              }}
            >
              Set your preferences now
            </div>
          </div>
        )}
        <div
          className={`section-container header-wrapper ${
            hasPaddingBottom && "header-width-padding-bottom"
          }`}
        >
          <div
            className={`header-logo-wrapper ${alwaysShowLogo && "plush-show"}`}
          >
            <Link href={showLogoOnly ? "" : "/"} className="z-[9999] relative">
              <div
                className={`${italiana.className} header-logo-text z-[9999] relative`}
                style={{ display: "flex", color: "#191919" }}
              >
                Plush
              </div>
            </Link>
          </div>

          {!showLogoOnly && (
            <>
              <div className="header-right">
                {me && me.privateInfo.isPartner && (
                  <div className="header-right-share">
                    {isShareLoading ? (
                      <div className="loading-wrapper">
                        <Loading />
                      </div>
                    ) : (
                      <img
                        src={
                          isShareCopied ? "/copied-icon.svg" : "/share-icon.svg"
                        }
                        className=""
                        onClick={handleShare}
                        alt="share"
                      />
                    )}
                  </div>
                )}

                {me ? (
                  <div
                    className="header-right-collection"
                    onClick={() => goPage("collections")}
                  >
                    <HeartLineBlackThinIcon />
                    <div className="items-number">{itemsCount}</div>
                  </div>
                ) : (
                  <div className="sign-in" onClick={() => goLogin()}>
                    Sign In
                  </div>
                )}
              </div>
              <div className="header-center-options">
                {navigation.map((tab, index) => {
                  return !tab.page ? (
                    <div
                      key={tab.title}
                      className={`header-center-option`}
                      onMouseLeave={() => setShowDropdown(-1)}
                    >
                      <CategoryDropdown
                        show={showDropdown === index}
                        onClose={() => setShowDropdown(-1)}
                        categories={
                          (tab.categories as NavigatioCategory[]) || []
                        }
                        images={tab.images || []}
                      />
                      <span onMouseOver={() => setShowDropdown(index)}>
                        {tab.title}
                      </span>
                    </div>
                  ) : (
                    <div key={tab.title} className={`header-center-option`}>
                      <Link href={tab.page}>{tab.title}</Link>
                    </div>
                  );
                })}
              </div>
              <HeaderMenu />
              {/* Mobile Header Menu */}
              <>
                <MenuIcon onClick={handleShow} className="header-left-menu" />
                <Offcanvas
                  placement="top"
                  show={show}
                  onHide={handleClose}
                  backdrop="static"
                  keyboard={false}
                  backdropClassName="offcanvas-backdrop"
                  style={{
                    height: "100%",
                    width: "100%",
                    backgroundColor: "#fff",
                    zIndex: 99999,
                  }}
                >
                  <Offcanvas.Header
                    style={{ width: "100%", padding: `20px 24px 12px` }}
                  >
                    <Offcanvas.Title style={{ width: "100%" }}>
                      <div
                        className="flex justify-between items-center"
                        style={{ width: "100%" }}
                      >
                        <div
                          className="header-icon back-icon"
                          style={{
                            visibility: mobileMenu ? "visible" : "hidden",
                          }}
                          onClick={() => setMobileMenu("")}
                        >
                          <img src="/back.svg" alt="back"></img>
                        </div>
                        <div className="title">{mobileMenu}</div>
                        <div className="header-icon" onClick={handleClose}>
                          <img src="/blackClose.svg" alt="close"></img>
                        </div>
                      </div>
                    </Offcanvas.Title>
                  </Offcanvas.Header>
                  {!currentNavigation ? (
                    <Offcanvas.Body style={{ padding: 0 }}>
                      <div className="header-menu-content">
                        {me && (
                          <div className="user">
                            <div className={`${italiana.className} name`}>
                              Hi {me.name}
                            </div>
                          </div>
                        )}
                        <>
                          {navigation.map((tab) => {
                            return (
                              <div
                                key={tab.title}
                                className={`mobile-header-menu-option`}
                                style={{
                                  backgroundImage: `url(${tab.titleImageForMobile})`,
                                }}
                                onClick={() =>
                                  handleMobileOptionClick(tab as NavigationRow)
                                }
                              >
                                <span>{tab.title}</span>
                              </div>
                            );
                          })}
                        </>
                        <div className="divider"></div>
                        <div className="option-list">
                          <div className="option" onClick={() => goPage("/")}>
                            <img src="/home.svg" className="option-img" />
                            <div className="option-text">Home</div>
                          </div>
                          {me && (
                            <div
                              className="option"
                              onClick={() => goPage("collections")}
                            >
                              <img
                                src="/heart-line-black.svg"
                                className="option-img"
                              />
                              <div className="option-text">My collections</div>
                              <div className="option-right">{itemsCount}</div>
                            </div>
                          )}
                          <div
                            className="option"
                            onClick={() => goPage("about")}
                          >
                            <img src="/about.svg" className="option-img" />
                            <div className="option-text">About</div>
                          </div>
                          <div
                            className="option"
                            onClick={() => goPage("contact")}
                          >
                            <img src="/contact.svg" className="option-img" />
                            <div className="option-text">Contact</div>
                          </div>
                          {me && (
                            <div className="option" onClick={handleLogout}>
                              <img src="/logout.svg" className="option-img" />
                              <div className="option-text text-red">
                                Sign out
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="menu-bottom">
                          <div className="social-section">
                            <Link
                              href={SOCIAL_LINKS.instagram}
                              className="social-icon"
                              target="_blank"
                            >
                              <img src="/ins-black.svg" />
                            </Link>
                            <Link
                              href={SOCIAL_LINKS.tiktok}
                              className="social-icon"
                              target="_blank"
                            >
                              <img src="/tiktok-black.svg" />
                            </Link>
                            <Link
                              href={SOCIAL_LINKS.twitter}
                              className="social-icon"
                              target="_blank"
                            >
                              <img src="/twitter-black.svg" />
                            </Link>
                          </div>
                          {!me && (
                            <div className="sign-in">
                              <Button onClick={() => goPage("login")}>
                                Sign in
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Offcanvas.Body>
                  ) : (
                    <Offcanvas.Body style={{ padding: 0 }}>
                      <div className="header-menu-content ">
                        {currentNavigation.categories &&
                          currentNavigation.categories.map((category) => {
                            return !category.mobileImageFormat ? (
                              <div
                                className="collapsible-wrapper"
                                key={category.title}
                              >
                                <Collapsible
                                  key={category.title}
                                  title={category.title}
                                  items={category.items.map((item) => ({
                                    text: item.text,
                                    onClick: () => {
                                      router.push(
                                        `/results/${encodeURL(
                                          item.searchQuery
                                        )}`
                                      );
                                      closeMobileMenu();
                                    },
                                  }))}
                                />
                              </div>
                            ) : (
                              <div
                                className="list-wrapper"
                                key={category.title}
                              >
                                <div
                                  className={`${italiana.className} list-title`}
                                >
                                  {category.title}
                                </div>

                                <div className="list-content">
                                  {category &&
                                    category.items.map((item) => (
                                      <div
                                        key={item.searchQuery}
                                        className="item"
                                      >
                                        <Link
                                          href={`/results/${encodeURL(
                                            item.searchQuery
                                          )}`}
                                          onClick={closeMobileMenu}
                                        >
                                          {item.imageForMobile && (
                                            <img
                                              className="item-image"
                                              src={item.imageForMobile}
                                            />
                                          )}
                                          <span className="item-text">
                                            {item.text}
                                          </span>
                                        </Link>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </Offcanvas.Body>
                  )}
                </Offcanvas>
              </>
            </>
          )}
        </div>
      </div>
      <style jsx>{`
        .header {
          width: 100%;
        }
        .tips {
          width: 100%;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          height: 32px;
          background: linear-gradient(
            90.72deg,
            #efefef -5.34%,
            #bbbbbb 108.05%
          );
          z-index: 100;
          position: relative;

          font-size: 14px;
          font-weight: 400;
          letter-spacing: 0.25px;

          :global(.tips-link) {
            margin-left: 8px;
            font-weight: 700;
            text-decoration: underline;
            cursor: pointer;
          }

          @media (max-width: 480px) {
            flex-direction: column;
            height: 56px;
          }
        }
        .plush-show {
          display: block !important;
        }

        .header-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          height: 88px;
          padding: 40px 72px 0px;
          z-index: 9999;
          &.header-width-padding-bottom {
            @media (min-width: 769px) {
              padding-bottom: 32px;
              height: 120px;
            }
          }
        }

        .header-logo-wrapper {
          position: absolute;
          top: 40px;
          left: 72px;
          z-index: 9999;
          cursor: pointer;
        }

        .header-logo-text {
          font-size: 40px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }

        .header-center-options {
          display: flex;
          height: 100%;

          .header-center-option {
            display: flex;
            align-items: center;
            height: 100%;
            color: #191919;
            text-align: center;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
            letter-spacing: -0.28px;

            padding: 0 24px;

            & > span {
              cursor: pointer;
            }

            &:hover {
              text-decoration-line: underline;
              text-underline-offset: 10px;
              text-decoration-thickness: 2px;
            }
          }
        }

        .header-right {
          display: flex;
          align-items: center;
          position: absolute;
          top: 52px;
          right: 120px;
        }

        .header-right-collection {
          display: flex;
          align-items: center;
          cursor: pointer;

          .items-number {
            margin-left: 8px;
            color: #191919;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px;
          }
        }

        .header-right-share {
          display: flex;
          margin-right: 24px;
          align-items: center;
          cursor: pointer;
          width: 24px;
          height: 24px;
        }

        .loading-wrapper {
          transform: scale(0.5);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sign-in {
          color: #191919;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .header-logo-wrapper {
            display: none;
            top: 27px;
            left: 60px;
          }
          .header-wrapper {
            padding: 0;
            height: 72px;
          }
          .header-logo-text {
            line-height: 24px;
            font-size: 32px;
          }
          .header-center-options {
            display: none;
          }

          .header-right {
            top: 30px;
            right: 24px;
          }

          .header-right-share {
            margin-right: 12px;
          }

          .sign-in {
            color: #191919;
            font-size: 15px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            letter-spacing: -0.28px;
          }

          .title {
            color: #191919;
            text-align: center;
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            letter-spacing: -0.28px;
          }

          // .header-icon {
          //   width: 24px;
          //   height: 24px;
          //   > img {
          //     width: 100%;
          //     height: 100%;
          //   }
          // }

          .header-menu-content {
            padding: 0 24px;
            background-color: #fff;
            min-height: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;

            .collapsible-wrapper {
              &:last-child {
                margin-bottom: 24px;
              }
            }

            .user {
              width: 100%;
              margin-bottom: 12px;

              .name {
                color: #191919;
                font-size: 32px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
              }
            }
            .mobile-header-menu-option {
              width: 100%;
              height: 100px;
              padding: 0 24px;
              display: flex;
              align-items: center;
              margin-bottom: 12px;
              position: relative;
              background-size: cover;

              color: #191919;
              font-size: 14px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
              letter-spacing: -0.28px;
            }
            .menu-bottom {
              flex: auto;
              display: flex;
              flex-direction: column;
              justify-content: flex-end;

              .social-section {
                display: flex;
                justify-content: center;

                a {
                  width: 48px;
                  height: 48px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
              }

              .sign-in {
                padding-top: 24px;
                padding-bottom: 14px;

                display: flex;
                align-items: flex-end;
              }
            }

            .divider {
              margin: 24px 0;
              width: 100%;
              height: 1px;
              background: #e6e6e6;
            }
            .option-list {
              width: 100%;
              padding: 12px 0;

              .option {
                height: 48px;
                display: flex;
                align-items: center;
                cursor: pointer;
                position: relative;

                .option-img {
                  width: 16px;
                  height: 16px;
                  margin-right: 12px;
                }

                .option-text {
                  color: #191919;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: normal;
                  letter-spacing: -0.28px;

                  &.text-red {
                    color: #a20000;
                  }
                }

                .option-right {
                  position: absolute;
                  right: 0;

                  color: #666;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: normal;
                  letter-spacing: -0.28px;
                }
              }
            }
          }
          .list-wrapper {
            margin-bottom: 24px;
            .list-title {
              color: #191919;
              font-size: 20px;
              font-style: normal;
              font-weight: 400;
              line-height: normal;

              margin-bottom: 20px;
            }

            .list-content {
              display: flex;
              gap: 8px;
              width: 100%;
              flex-wrap: wrap;
              .item {
                width: calc(50% - 4px);
                .item-image {
                  aspect-ratio: 0.7 / 1;
                  margin-bottom: 7px;
                  object-fit: cover;
                }
                .item-text {
                  color: var(--Deck-Colors-1, #191919);
                  text-align: center;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 600;
                  line-height: normal;
                }
              }
            }
          }
        }
      `}</style>
      <style jsx global>{`
        .header-left-menu {
          display: none;

          @media (max-width: 768px) {
            position: absolute;
            display: block;
            top: 26px;
            left: 24px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
